import React, { useMemo, memo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ImageLibraryItem } from '@/hooks/runware/image-library';
import ImageCard from '../ImageCard';
import { SkeletonImageCard } from './SkeletonImageCard';
import { useImageAnimationVariants } from '@/hooks/ui/use-image-animation-variants';
import { useImageLoading } from '@/hooks/image-generator/use-image-loading';
import { useImageCache } from '@/hooks/image-generator/use-image-cache';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';

interface ImageGridProps {
  images: ImageLibraryItem[];
  onDeleteImage: (id: string) => void;
  onSelectImage: (image: ImageLibraryItem) => void;
  isLoading?: boolean;
  contentType?: 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image'; // NEW: Optional contentType prop
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage buttons
}

export const ImageGrid: React.FC<ImageGridProps> = memo(({ 
  images, 
  onDeleteImage,
  onSelectImage,
  isLoading = false,
  contentType = "image", // NEW: Default to "image"
  hideStorageButton = false // NEW: Default to false
}) => {
  const { gridContainerVariants } = useImageAnimationVariants();

  // Always show skeletons during loading, regardless of images presence
  const showSkeletons = isLoading;
  const skeletonCount = Math.max(9, images.length); // Show at least 9 skeletons or match image count
  
  // Use custom hooks for image loading state and caching
  const { loadedImages, handleImageLoaded, resetLoadingState } = useImageLoading();
  const { preloadImages } = useImageCache();

  // Reset loaded images state when isLoading changes to true
  useEffect(() => {
    if (isLoading) {
      resetLoadingState();
    }
  }, [isLoading, resetLoadingState]);
  
  // Preload images for better user experience
  useEffect(() => {
    if (images.length > 0 && !isLoading) {
      // Get all image URLs
      const imageUrls = images.map(img => img.image_url).filter(Boolean) as string[];
      
      // Preload the first 6 images immediately for faster initial display
      const initialBatch = imageUrls.slice(0, 6);
      if (initialBatch.length > 0) {
        preloadImages(initialBatch);
      }
      
      // Preload the rest after a short delay
      if (imageUrls.length > 6) {
        const remainingBatch = imageUrls.slice(6);
        const timer = setTimeout(() => {
          preloadImages(remainingBatch);
        }, 1000); // 1 second delay
        
        return () => clearTimeout(timer);
      }
    }
  }, [images, isLoading, preloadImages]);

  // Memoize the grid container variants to prevent recalculation on every render
  const containerVariants = useMemo(() => gridContainerVariants(), []);
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      // Remove layout animation to improve performance
      layout={false}
      className="relative"
      style={{ willChange: 'opacity' }} // Add hardware acceleration
    >
      <motion.div 
        className={`grid ${GRID_BREAKPOINTS.imageCards} gap-1`} 
        data-test-id="image-grid"
        // Use simpler layout animations to improve performance
        layout={false}
      >
        {/* Render both skeletons and images in parallel for smooth transitions */}
        {useMemo(() => {
          // Create a map of positions for consistent placement
          return images.map((image, index) => (
            <div className="relative" key={image.id}>
              {/* Skeleton that fades out as image loads */}
              {showSkeletons && (
                <motion.div
                  className="absolute inset-0 z-10"
                  initial={{ opacity: 1 }}
                  animate={{ 
                    opacity: loadedImages[image.id] ? 0 : 1,
                    transition: { duration: 0.3 }
                  }}
                  style={{ 
                    willChange: 'opacity', 
                    pointerEvents: loadedImages[image.id] ? 'none' : 'auto' 
                  }}
                >
                  <SkeletonImageCard index={index} />
                </motion.div>
              )}
              
              {/* Image card that fades in over the skeleton */}
              <motion.div
                initial={{ opacity: showSkeletons ? 0 : 1 }}
                animate={{ 
                  opacity: showSkeletons ? (loadedImages[image.id] ? 1 : 0) : 1,
                  transition: { duration: 0.3, delay: 0.05 }
                }}
                className={loadedImages[image.id] ? "z-20 relative" : ""}
                style={{ willChange: 'opacity' }}
              >
                <ImageCard
                  image={image}
                  onDelete={onDeleteImage}
                  onSelect={onSelectImage}
                  index={index}
                  onImageLoaded={() => handleImageLoaded(image.id)}
                  contentType={contentType}
                  hideStorageButton={hideStorageButton}
                />
              </motion.div>
            </div>
          ));
        }, [images, showSkeletons, loadedImages, contentType, hideStorageButton])}
        
        {/* Show placeholder skeletons when no images or during initial load */}
        {useMemo(() => {
          if (showSkeletons && images.length === 0) {
            return Array.from({ length: skeletonCount }).map((_, index) => (
              <SkeletonImageCard key={`skeleton-placeholder-${index}`} index={index} />
            ));
          }
          return null;
        }, [showSkeletons, images.length, skeletonCount])}
      </motion.div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Only re-render if images array reference changes, loading state changes, or array length changes
  if (prevProps.isLoading !== nextProps.isLoading) {
    return false; // Always re-render when loading state changes
  }
  
  // Fast path: same reference means same content
  if (prevProps.images === nextProps.images) {
    return true;
  }
  
  // If lengths differ, content must be different
  if (prevProps.images.length !== nextProps.images.length) {
    return false;
  }
  
  // Deep comparison of image IDs only (not entire objects)
  return prevProps.images.every((img, i) => img.id === nextProps.images[i].id);
});
